import { queryClient } from "@/integrations/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { Suspense, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";

import HeadTags from "@/app/seo/HeadTags";
import { MotionProvider } from "@/components/providers/MotionProvider";
import { ThemeProvider } from "@/components/providers/ThemeProvider";
import ServiceWorkerUpdate from "@/components/ServiceWorkerUpdate";
import { TelegramProvider } from "@/components/telegram";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { PageTransitionLayer } from "@/components/ui/PageTransitionLayer";
import { ScreenReaderLive } from "@/components/ui/ScreenReaderLive";
import { ScrollToTop } from "@/components/ui/ScrollToTop";
import { SkipLink } from "@/components/ui/SkipLink";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import VersionBadge from "@/components/VersionBadge";

// Phase 5.1: Lazy load routes - REVERTED due to white screen issue
// TODO: Debug lazy loading routes properly
import { AdminRoutes } from "@/routes/adminRoutes";
import { PublicRoutes } from "@/routes/publicRoutes";

import { PublicChatKitButtonWrapper } from "@/components/public/PublicChatKitButtonWrapper";
import { RealtimeEventHub } from "@/features/realtime/RealtimeEventHub";
import { isAdminDomain } from "@/lib/domains/admin";
import { guardEnvForDomain, validateEnvAtBuildTime } from "@/lib/env/env-guard";
import { initializeSecurity } from "@/lib/security/init";
import { initSW } from "@/lib/swUpdater";
import { buildValidator } from "@/utils/buildValidator";
import { resolveBuildMismatch } from "@/utils/resolveBuildMismatch";

// Минимальный fallback без хуков/заявок — только статический markup
function GlobalSuspenseFallback() {
  const admin = isAdminDomain();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div
        className="text-sm text-muted-foreground"
        data-testid={admin ? "admin-global-suspense" : "global-suspense"}
      >
        {admin ? "Loading admin..." : "Loading..."}
      </div>
    </div>
  );
}

function GlobalErrorFallback({ error, errorId, onRetry }: { error?: Error; errorId?: string; onRetry?: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full space-y-4 text-center">
        <div className="text-destructive font-semibold text-lg">App Unavailable</div>
        <p className="text-sm text-muted-foreground">
          The application encountered an error and cannot continue. Please try reloading the page.
        </p>
        {error && (
          <details className="text-left text-xs text-muted-foreground bg-muted p-3 rounded-md">
            <summary className="cursor-pointer font-medium mb-2">Error details {errorId && `(ID: ${errorId})`}</summary>
            <pre className="mt-2 overflow-auto max-h-32 whitespace-pre-wrap break-words">
              {error.message}
            </pre>
          </details>
        )}
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 rounded-md border border-primary bg-primary text-primary-foreground hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Try Again
            </button>
          )}
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    // Безопасность/инфра
    initializeSecurity();
    validateEnvAtBuildTime();
    guardEnvForDomain();

    (async () => {
      buildValidator.logBuildInfo();
      const isProd = import.meta.env.PROD || import.meta.env.VITE_ENV_LABEL === "PRD";
      if (isProd) {
        await initSW();
        const validation = await buildValidator.validateBuildConsistency();
        if (!validation.isValid) {
          console.warn("[App] Build validation detected issues:", validation.warnings);
          await resolveBuildMismatch(validation);
        }
      }
    })().catch(console.error);
  }, []);

  const isAdminSurface = isAdminDomain();

  const appShell = (
    <MotionProvider>
      <TooltipProvider>
        <Analytics />
        <ServiceWorkerUpdate />
        <SkipLink />
        <ScreenReaderLive />
        <Toaster />
        <VersionBadge />
        <HeadTags />
        <RealtimeEventHub
          enabled={true}
          debug={!(import.meta.env.PROD || import.meta.env.VITE_ENV_LABEL === "PRD")}
        />
        <ErrorBoundary
          level="page"
          onError={(error, errorInfo) => {
            // Log error for debugging
            console.error('[App] Global error boundary caught:', error, errorInfo);
            // Also log to Sentry if available
            if (typeof window !== 'undefined' && (window as any).Sentry) {
              (window as any).Sentry.captureException(error, {
                contexts: {
                  react: {
                    componentStack: errorInfo.componentStack
                  }
                }
              });
            }
          }}
        >
          <BrowserRouter>
            <ScrollToTop />
            <PageTransitionLayer />
            {/* Public ChatKit Button - только на публичных страницах, внутри BrowserRouter */}
            <PublicChatKitButtonWrapper />
            <Suspense fallback={<GlobalSuspenseFallback />}>
              {isAdminSurface ? (
                <AdminRoutes isAdminDomain={isAdminSurface} />
              ) : (
                <PublicRoutes isAdminDomain={isAdminSurface} />
              )}
            </Suspense>
          </BrowserRouter>
        </ErrorBoundary>
      </TooltipProvider>
    </MotionProvider>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TelegramProvider>{appShell}</TelegramProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
